<script context="module">
  import ProductVariations from "./ProductVariations.svelte";
  import {External, M} from "../../external";

  export function editProductVariations(product, onDone) {
    External.Tools.showWindow({
      title: M.STORE_VARIATIONS,
      width: product?.type === External.STORE_PRODUCT_TYPE.PHYSICAL_PRODUCT ? 1100 : 900,
      isModal: false,
      withDarkBackground: true,
      forceAtRight: External.showBuilderPopupsOnFullHeight,
      action: {
        onShow: (popup, context) => {
          popup.bindComponent(
            new ProductVariations({
              target: popup.getContentArea(),
              props: {
                product: product
              }
            })
          );
        },
        onHide: (popup, ui) => {
          onDone();
        }
      }
    });
  }
</script>

<script>
  import { onMount } from "svelte";
  import { makeWebsiteRequest } from "../../Common/tools";

  export let product;

  product.variations = product.variations || [];
  product.combinations = product.combinations || [];

  let variationSuggestions = [];
  let vriationValueSuggestions = {};
  let addingVariation = false;

  let isPhysicalProduct = product.type === External.STORE_PRODUCT_TYPE.PHYSICAL_PRODUCT;
  let hasQuantity = true; // product.type !== STORE_PRODUCT_TYPE.DIGITAL_PRODUCT && product.type !== STORE_PRODUCT_TYPE.MEMBERSHIP;

  let setupOptionAutocomplete = function (domNode) {
    let node = External.$(domNode);
    let input = node.find("input");

    let source = variationSuggestions.slice();
    for (let idx = 0; idx < product.variations.length; ++idx) {
      let i = source.indexOf(product.variations[idx].name);
      if (i > -1) {
        source.splice(i, 1);
      }
    }

    node.on("mousedown", "a", () => {
      addOption(input.val());
      addingVariation = false;
    });

    input
      .autocomplete({
        minLength: 0,
        source: source,
        classes: {
          "ui-autocomplete": "product-option-autocomplete"
        },
        select: function (event, ui) {
          addOption(ui.item.value);
          addingVariation = false;
        }
      })
      .keyup(function (event) {
        event.stopPropagation();
        event.preventDefault();

        if (event.which === 27) {
          addingVariation = false;
        } else if (event.which === 13) {
          addOption(input.val());
          addingVariation = false;
        }
      })
      .blur(function () {
        addingVariation = false;
      })
      .focus()
      .autocomplete("search", "");

    return {
      destroy: () => {
        input.autocomplete("destroy");
        node.off();
        input = null;
        node = null;
      }
    };
  };

  let setupOptionValuesAutocomplete = (inputNode, variationIdx) => {
    let input = External.$(inputNode);
    let variation = product.variations[variationIdx];

    let selectOptionValue = (value) => {
      value = value.replace(/\,/gi, "");
      setTimeout(() => {
        input.val("").autocomplete("search");
      }, 10);

      if (variation.values.indexOf(value) > -1) {
        return;
      }
      variation.values.push(value);
      if (variation.advanced) {
        refreshCombinations();
      }
      product.variations[variationIdx].values = product.variations[variationIdx].values;
    };

    input
      .autocomplete({
        minLength: 0,
        source: async (request, response) => {
          let suggest = () => {
            let items = vriationValueSuggestions[variation.name].items || [];
            let suggestions = [];
            let term = request.term.toLowerCase();
            let includeTermInResults = true;

            for (let idx = 0; idx < items.length; ++idx) {
              let item = items[idx];
              let suggestion = items[idx].toLowerCase();

              if (suggestion === term) {
                includeTermInResults = false;
              }

              if (suggestion.indexOf(term) >= 0 && variation.values.indexOf(item) === -1) {
                suggestions.push(items[idx]);
              }
            }
            if (includeTermInResults) {
              suggestions.unshift(request.term);
            }
            response(suggestions);
          };

          let suggestions = vriationValueSuggestions[variation.name] || { loaded: false, items: [] };

          if (suggestions.loaded) {
            if (suggestions.items.length > 0) {
              suggest();
            }
          } else {
            suggestions.loaded = true;
            let result = await makeWebsiteRequest({
              action: "getSuggestions",
              data: {
                type: "store-variation-values",
                key: variation.name,
                prefix: ""
              }
            });

            vriationValueSuggestions[variation.name] = { loaded: true, items: result.data };
            if (result.data.length > 0) {
              suggest();
            }
          }
        },
        classes: {
          "ui-autocomplete": "product-option-autocomplete"
        },
        select: (event, ui) => {
          selectOptionValue(ui.item.value);
        }
      })
      .keypress((event) => {
        let enter = event.which === 13;
        let isComma = String.fromCharCode(event.which) === ",";
        if (enter || isComma) {
          selectOptionValue(input.val());
        }
      })
      .focus(() => {
        input.autocomplete("search");
      });

    return {
      destroy: () => {
        input.autocomplete("destroy");
        input = null;
      }
    };
  };

  let addOption = (option) => {
    if (option.trim() === "") return;
    let variation = {
      name: option,
      values: [],
      advanced: false
    };
    product.variations.push(variation);
    product.variations = product.variations;
  };

  let removeVariationValue = (variationIdx, valueIdx) => {
    let variation = product.variations[variationIdx];
    variation.values.splice(valueIdx, 1);
    if (variation.advanced) {
      refreshCombinations();
    }
    product.variations[variationIdx].values = product.variations[variationIdx].values;
  };

  let removeVariation = (variationIdx) => {
    External.Tools.prompt("", M.DEL_VARIATION_MESSAGE, () => {
      let variation = product.variations[variationIdx];
      product.variations.splice(variationIdx, 1);
      if (variation.advanced) {
        refreshCombinations();
      }
      product.variations = product.variations;
    });
  };

  let setVariationImages = (variationIdx) => {
    let variation = product.variations[variationIdx];
    External.UITools.pickProductVariationImages(variation);
  };

  let refreshCombinations = function () {
    let advancedVariations = product.variations.filter((variation) => variation.advanced).map((variation) => variation.values);

    let oldCombinations = product.combinations || [];
    product.combinations = [];

    if (advancedVariations.length > 0) {
      External.WebPlatform.cartesianProduct(advancedVariations, (combination) => {
        product.combinations.push({
          name: combination.join(" ● "),
          price: product.price,
          salePrice: product.salePrice,
          onSale: product.onSale,
          sku: product.sku,
          quantity: "",
          weight: product.weight,
          width: product.width,
          height: product.height,
          length: product.length
        });
      });

      // try to restore previous combinations
      oldCombinations.forEach((oldCombination) => {
        for (let idx = 0; idx < product.combinations.length; ++idx) {
          let newCombination = product.combinations[idx];
          if (newCombination.name === oldCombination.name) {
            newCombination.price = oldCombination.price;
            newCombination.salePrice = oldCombination.salePrice;
            newCombination.onSale = oldCombination.onSale;
            newCombination.sku = oldCombination.sku;
            newCombination.quantity = oldCombination.quantity;
            newCombination.weight = oldCombination.weight;
            newCombination.width = oldCombination.width;
            newCombination.height = oldCombination.height;
            newCombination.length = oldCombination.length;
            break;
          }
        }
      });
    }

    product.combinations = product.combinations;
  };

  onMount(async () => {
    let result = await makeWebsiteRequest({
      action: "getSuggestions",
      data: {
        type: "store-variation-names", //store-variation-values
        key: "",
        prefix: ""
      }
    });
    variationSuggestions = result.data;
  });
</script>

<div class="members-wrapper">
  <div class="store-products product-content col20 f-left">
    <div class="box col20 variations-box f-left col20 f-left" style="width: 100%">
      {#if product.variations && product.variations.length}
        <table class="variation-info add-variation-table col20 f-left">
          <thead>
            <tr>
              <th width="100px;">{M.VARIATION_NAME}</th>
              <th>{M.VALUES}</th>
              <th width="50px;" class="button-tooltip" data-timeout="6000" data-tooltip="storeProductAdvancedVariationTooltip">{M.ADVANCED}</th>
              <th width="50px;" />
            </tr>
          </thead>
          <tbody>
            {#each product.variations as variation, idx}
              <tr>
                <td width="80px;"><span class="variation-name" />{variation.name}</td>
                <td>
                  <ul class="variation-tags tags-wrapper f-left col20">
                    {#each variation.values as value, valueIdx}
                      <li class="tagit-choice">
                        <span>{value}</span>
                        <a on:click={() => removeVariationValue(idx, valueIdx)}>
                          <span class="ml-5">×</span>
                        </a>
                      </li>
                    {/each}
                    <input type="text" placeholder={M.STORE_PRODUCT_VARIATIONS_PLACEHOLDER} autocomplete="off" use:setupOptionValuesAutocomplete={idx} />
                  </ul>
                </td>
                <td width="50px;">
                  <div class="checkbox-wrapper f-left col20">
                    <input id={"advanced-variation" + idx} class="css-checkbox" type="checkbox" bind:checked={variation.advanced} on:change={refreshCombinations} />
                    <label
                      for={"advanced-variation" + idx}
                      style="margin-right: 0"
                      class="css-label button-tooltip"
                      data-timeout="6000"
                      data-tooltip="storeProductAdvancedVariationTooltip">
                      &nbsp;
                    </label>
                  </div>
                </td>
                <td width="50px;">
                  <a class="set-image button-tooltip" data-tooltip="variationImages" style="margin-right: 15px;" on:click={() => setVariationImages(idx)}>
                    <i class="fa fa-picture-o" />
                  </a>
                  <a class="delete" on:click={() => removeVariation(idx)}>
                    <i class="fa fa-trash-o" />
                  </a>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      {/if}
      {#if addingVariation}
        <div class="inline-popup-wrapper" use:setupOptionAutocomplete>
          <h4>{M.ADD_VARIATION_NAME_HINT}</h4>
          <input type="text" class="tag-autocomplete" />
          <a class="plus-sign" style="margin-top: 11px" />
        </div>
      {/if}
      <a
        on:click={() => {
          addingVariation = true;
        }}
        class="gray middle-button transition-bg f-left clear-left">{M.ADD_VARIATION}</a>

      {#if product.combinations.length}
        <div class="col20 f-left combination-table-wrapper mt-25">
          <table class="variation-info col20 f-left">
            <thead>
              <tr>
                <th width="20%">{M.COMBINATION}</th>
                <th>{M.STORE_PRODUCT_PRICE}</th>
                <th>{M.STORE_PRODUCT_SALE_PRICE}</th>
                <th width="50px">{M.PRODUCT_ON_SALE}</th>
                <th>{M.SKU}</th>
                {#if hasQuantity}
                  <th>{M.AVAILABLE_IN_STOCK}</th>
                {/if}
                {#if isPhysicalProduct}
                  <th>{M.WEIGHT}</th>
                  <th>{M.WIDTH}</th>
                  <th>{M.HEIGHT}</th>
                  <th>{M.LENGTH}</th>
                {/if}
              </tr>
            </thead>
            <tbody>
              {#each product.combinations as combination, combinationIdx}
                <tr>
                  <td style="width: 150px"><span>{combination.name}</span></td>
                  <td>
                    <div class="symbol-input variation-price" class:left-sign={External.Website.storeCurrencyPositionAtLeft} class:right-sign={!External.Website.storeCurrencyPositionAtLeft}>
                      <span class="symbol-sign">{External.Website.storeCurrencySymbol}</span>
                      <input type="number" min="0.00" step="0.01" placeholder="0.00" bind:value={combination.price} />
                    </div>
                  </td>
                  <td>
                    <div class="symbol-input variation-price" class:left-sign={External.Website.storeCurrencyPositionAtLeft} class:right-sign={!External.Website.storeCurrencyPositionAtLeft}>
                      <span class="symbol-sign">{External.Website.storeCurrencySymbol}</span>
                      <input type="number" min="0.00" step="0.01" placeholder="0.00" bind:value={combination.salePrice} />
                    </div>
                  </td>
                  <td>
                    <div class="checkbox-wrapper f-left col20">
                      <input id={"on-sale-" + combinationIdx} class="css-checkbox" type="checkbox" bind:checked={combination.onSale} />
                      <label for={"on-sale-" + combinationIdx} class="css-label">&nbsp;</label>
                    </div>
                  </td>
                  <td>
                    <input type="text" bind:value={combination.sku} />
                  </td>
                  {#if hasQuantity}
                    <td><input type="number" min="0" step="1" bind:value={combination.quantity} /></td>
                  {/if}
                  {#if isPhysicalProduct}
                    <td>
                      <div class="symbol-input right-sign variation-price">
                        <span class="symbol-sign">{External.Website.storeWeightUnit}</span>
                        <input type="number" min="0" step="1" bind:value={combination.weight} />
                      </div>
                    </td>
                    <td>
                      <div class="symbol-input right-sign variation-price">
                        <span class="symbol-sign">{External.Website.storeDimensionsUnit}</span>
                        <input type="number" min="0" step="0.1" bind:value={combination.width} />
                      </div>
                    </td>
                    <td>
                      <div class="symbol-input right-sign variation-price">
                        <span class="symbol-sign">{External.Website.storeDimensionsUnit}</span>
                        <input type="number" min="0" step="0.1" bind:value={combination.height} />
                      </div>
                    </td>
                    <td>
                      <div class="symbol-input right-sign variation-price">
                        <span class="symbol-sign">{External.Website.storeDimensionsUnit}</span>
                        <input type="number" min="0" step="0.1" bind:value={combination.length} />
                      </div>
                    </td>
                  {/if}
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  :global(.product-option-autocomplete) {
    max-height: 265px;
    overflow: auto;
  }
</style>
